import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, assetUrl, errorText, useFetch } from "../api";
import { Alerts, Badge, Field, Loading, Page, Pager, Tabs, UserLink } from "../components/ui";
import { PAGE_SIZE, qs, setQuery, when } from "../format";

export default function Tickets() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const status = params.get("status") || "open";
  const list = useFetch("/tickets" + qs({ page, limit: PAGE_SIZE, status }));
  const detail = useFetch(id ? "/tickets/" + id : "");
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];
  const ticket = detail.data?.data;
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState({});
  const thread = useRef(null);
  const fileInput = useRef(null);

  useEffect(() => {
    if (thread.current) thread.current.scrollTop = thread.current.scrollHeight;
  }, [ticket]);

  async function send(event) {
    event.preventDefault();
    if (file && file.size > 2 * 1024 * 1024) {
      setErrors({ attachment: "Image must be 2MB or smaller" });
      return;
    }
    setBusy("reply");
    setFormError("");
    setErrors({});
    const body = new FormData();
    body.append("message", message.trim());
    if (file) body.append("attachment", file);
    try {
      await api("/tickets/" + id + "/messages", { method: "POST", body });
      setMessage("");
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      setNotice("Reply sent");
      detail.reload();
      list.reload();
    } catch (err) {
      setFormError(errorText(err));
      setErrors(err.errors || {});
    } finally {
      setBusy("");
    }
  }

  async function saveStatus(next) {
    setBusy("status");
    setFormError("");
    try {
      await api("/tickets/" + id, { method: "PATCH", body: { status: next } });
      setNotice(next === "closed" ? "Ticket closed" : "Ticket opened");
      detail.reload();
      list.reload();
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setBusy("");
    }
  }

  const query = qs({ status, page: page > 1 ? page : "" });

  return (
    <Page title="Support" text="Your replies sit on the left. Member replies sit on the right. A reply notifies the member.">
      <Alerts error={list.error || detail.error || formError} notice={notice} />
      <Tabs
        value={status}
        onChange={(value) => setQuery(params, setParams, { status: value, page: "" })}
        options={[
          { value: "open", label: "Open" },
          { value: "closed", label: "Closed" },
        ]}
      />
      <div className="tickets">
        <div>
          <div className="ticket-list">
            {list.loading ? <Loading /> : null}
            {rows.map((row) => (
              <Link key={row.id} className={String(row.id) === String(id) ? "ticket-item active" : "ticket-item"} to={"/admin/tickets/" + row.id + query}>
                <strong>{row.subject}</strong>
                <span>{row.user ? row.user.name : "Member"} · {when(row.updatedAt)}</span>
                <Badge value={row.status} />
              </Link>
            ))}
            {!list.loading && !list.error && !rows.length ? <p className="hint pad">No tickets</p> : null}
          </div>
          {list.data ? (
            <Pager page={page} limit={PAGE_SIZE} total={list.data.total} onPage={(next) => setQuery(params, setParams, { page: next <= 1 ? "" : next })} />
          ) : null}
        </div>
        <div className="chat">
          {!id ? <p className="hint pad">Select a ticket.</p> : null}
          {id && detail.loading ? <Loading /> : null}
          {ticket ? (
            <>
              <div className="chat-head">
                <div>
                  <h2>{ticket.subject}</h2>
                  <UserLink user={ticket.user} userId={ticket.userId} />
                </div>
                <Field label="Status">
                  <select value={ticket.status} disabled={busy === "status"} onChange={(event) => saveStatus(event.target.value)}>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </Field>
              </div>
              <div className="bubbles" ref={thread}>
                {(ticket.messages || []).map((item, index) => (
                  <div key={item.createdAt + "-" + index} className={"bubble " + (item.senderType === "admin" ? "admin" : "user")}>
                    <p>{item.message}</p>
                    {item.attachment ? (
                      <a href={assetUrl(item.attachment)} target="_blank" rel="noreferrer">
                        <img src={assetUrl(item.attachment)} alt="" />
                      </a>
                    ) : null}
                    <time>{when(item.createdAt)}</time>
                  </div>
                ))}
              </div>
              <form className="chat-form" onSubmit={send}>
                <Field label="Reply" error={errors.message}>
                  <textarea maxLength={2000} value={message} onChange={(event) => setMessage(event.target.value)} />
                </Field>
                <Field label="Image" error={errors.attachment} hint="Optional, max 2MB">
                  <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" onChange={(event) => setFile((event.target.files && event.target.files[0]) || null)} />
                </Field>
                <div className="form-actions">
                  <button className="btn" disabled={busy === "reply"}>{busy === "reply" ? "Sending..." : "Send reply"}</button>
                </div>
              </form>
            </>
          ) : null}
        </div>
      </div>
    </Page>
  );
}
